import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';

import { getFailedDevices } from '../api/canary';
import RelativeTime from './RelativeTime';
import StatusBadge from './StatusBadge';
import { errorText } from '../lib/errorText';
import { STROKE, TEXT } from '../theme/tokens';

// FD-372 — the wave's failed devices (FD-369's read), on the completed-wave
// view. This is the H2 retry set: `salvaged` + `faulted`, never `aborted`
// (excluded server-side by design — aborted devices were deliberately stopped).
//
// Rendered when the wave is revoked because that is when the question becomes
// actionable: "the wave finished — which devices did NOT make it, and what were
// they doing when they failed?" The `failedActionId` column is the answer an
// operator takes to the event log; `salvageRecordOpen` says the FD-ADR-033
// recovery password is retained and revealable.

export interface WaveFailedDevicesPanelProps {
  migrationId: string;
}

export default function WaveFailedDevicesPanel({ migrationId }: WaveFailedDevicesPanelProps) {
  const query = useQuery({
    queryKey: ['failed-devices', migrationId],
    queryFn: () => getFailedDevices(migrationId),
  });

  return (
    <div
      className="flex flex-col gap-2 border-t pt-4"
      style={{ borderColor: STROKE.base }}
      aria-label="Failed devices"
    >
      <div className="flex items-baseline gap-2">
        <h3
          className="font-display text-[13px] font-semibold tracking-tight"
          style={{ color: TEXT.primary }}
        >
          Failed devices
        </h3>
        {query.data && (
          <span className="font-mono text-xs tabular-nums" style={{ color: TEXT.secondary }}>
            {query.data.count}
          </span>
        )}
      </div>

      {query.isLoading ? (
        <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          Loading failed devices…
        </p>
      ) : query.isError ? (
        <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          Failed devices are temporarily unavailable — {errorText(query.error)}
        </p>
      ) : query.data && query.data.count === 0 ? (
        <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          No devices failed in this wave — nothing to retry.
        </p>
      ) : query.data ? (
        <div
          className="overflow-x-auto"
          style={{ border: `1px solid ${STROKE.base}`, borderRadius: '6px' }}
        >
          <Table size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Serial</TableHeaderCell>
                <TableHeaderCell>Outcome</TableHeaderCell>
                <TableHeaderCell>Failed action</TableHeaderCell>
                <TableHeaderCell>When</TableHeaderCell>
                <TableHeaderCell>Recovery</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {query.data.failedDevices.map((d) => (
                <TableRow key={d.serialNumber}>
                  <TableCell>
                    <span className="font-mono text-xs">{d.serialNumber}</span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={d.terminalOutcome} />
                  </TableCell>
                  <TableCell>
                    {d.failedActionId ? (
                      <span className="font-mono text-xs">{d.failedActionId}</span>
                    ) : (
                      <span style={{ color: TEXT.disabled }}>—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
                      <RelativeTime iso={d.terminalUtc} />
                    </span>
                  </TableCell>
                  <TableCell>
                    {d.salvageRecordOpen ? (
                      // FD-ADR-033 — an open salvage record: the recovery-admin
                      // password is retained until this device completes a later
                      // migration. Amber = needs an operator, not a crash.
                      <Badge appearance="tint" color="warning">
                        password retained
                      </Badge>
                    ) : (
                      <span style={{ color: TEXT.disabled }}>—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : null}
    </div>
  );
}
